<template>
  <div class="min-h-screen w-full md:py-10">
    <div
      class="mx-auto flex min-h-screen w-full flex-col bg-white text-blue-900 md:min-h-[calc(100vh-5rem)] md:max-w-[480px] md:rounded-big md:shadow-xl"
    >
      <div class="flex-1 px-5 pb-8 pt-5">
        <div class="mb-4">
          <div class="text-[15px] font-bold">
            {{ currentLocation.name }}
          </div>
          <div v-if="currentLocation.address" class="text-[13px] text-gray-400">
            {{ currentLocation.address }}
          </div>
        </div>

        <div class="mb-5 flex gap-2">
          <button
            v-for="(step, index) in steps"
            :key="step.key"
            class="h-1.5 flex-1 rounded-full transition"
            :class="index <= currentStepIndex ? '' : 'bg-gray-200'"
            :style="
              index <= currentStepIndex
                ? { backgroundColor: buttonColor }
                : null
            "
            :disabled="index >= currentStepIndex"
            :aria-label="step.label"
            type="button"
            @click="goToStep(index)"
          />
        </div>

        <div
          class="mb-3 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
        >
          {{ currentStep.label }}
        </div>

        <div v-if="currentStep.key === 'service'">
          <div v-if="loading" class="flex justify-center py-10">
            <c-loading-spinner />
          </div>
          <div v-else class="space-y-3">
            <button
              v-for="service in services"
              :key="service.id"
              class="flex w-full items-center gap-3 rounded-small border bg-white p-4 text-left text-blue-900 transition"
              :class="serviceIsSelected(service) ? '' : 'border-gray-200'"
              :style="serviceCardStyle(service)"
              type="button"
              @click="handleServiceSelected(service)"
            >
              <template v-if="showServiceImages">
                <img
                  v-if="service.imageId"
                  class="h-16 w-16 shrink-0 rounded-small object-cover"
                  :src="serviceImageUrl(service)"
                  :alt="service.name"
                />
                <div
                  v-else
                  class="h-16 w-16 shrink-0 rounded-small border border-gray-200 bg-gray-100"
                />
              </template>
              <div class="flex min-w-0 flex-1 flex-col gap-1">
                <div class="text-base font-bold">
                  {{ service.name }}
                </div>
                <div
                  v-if="service.description"
                  class="text-[13px] leading-snug text-gray-400"
                >
                  {{ service.description }}
                </div>
              </div>
              <div class="flex shrink-0 flex-col items-end gap-0.5">
                <div class="font-bold">
                  {{ $filters.currency(service.price) }}
                </div>
                <div class="text-xs text-blue">
                  {{ service.durationMinutes }} min
                </div>
              </div>
              <span
                class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                :class="serviceIsSelected(service) ? '' : 'text-gray-300'"
                :style="
                  serviceIsSelected(service)
                    ? { backgroundColor: buttonColor, color: buttonTextColor }
                    : null
                "
              >
                <c-icon name="CheckRound" size="0.9rem" />
              </span>
            </button>
          </div>
        </div>

        <div v-else-if="currentStep.key === 'datetime'" class="space-y-5">
          <div
            v-if="showEmployeeSelector"
            class="-mx-5 flex gap-4 overflow-x-auto px-5 pb-1 pt-0.5"
          >
            <button
              v-for="employee in employeeSelectorOptions"
              :key="employee.id || 'anyone'"
              class="flex min-w-[64px] flex-col items-center gap-2"
              type="button"
              @click="handleEmployeeSelected(employee.id)"
            >
              <div
                class="relative flex h-14 w-14 items-center justify-center rounded-full border-2 bg-gray-100 text-[17px] font-bold text-blue"
                :class="
                  employee.id === selectedEmployeeId ? '' : 'border-gray-200'
                "
                :style="
                  employee.id === selectedEmployeeId
                    ? { borderColor: buttonColor }
                    : null
                "
              >
                <img
                  v-if="employee.photoImageId"
                  class="h-full w-full rounded-full object-cover"
                  :src="employeePhotoUrl(employee)"
                  :alt="employee.name"
                />
                <c-icon
                  v-else-if="!employee.id"
                  name="PersonFilled"
                  size="1.9rem"
                  class="text-gray-300"
                />
                <span v-else>{{ employeeInitial(employee) }}</span>
                <div
                  v-if="employee.id === selectedEmployeeId"
                  class="absolute -right-1 -top-1 flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 border-white"
                  :style="{
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                  }"
                >
                  <c-icon name="CheckRound" size="0.75rem" />
                </div>
              </div>
              <div
                class="max-w-[80px] truncate text-[13px]"
                :class="
                  employee.id === selectedEmployeeId
                    ? 'font-bold text-blue-900'
                    : 'text-gray-400'
                "
              >
                {{ employee.name }}
              </div>
            </button>
          </div>

          <div class="rounded-small border border-gray-200 px-3 pb-2 pt-3">
            <div class="mb-2 flex items-center justify-between">
              <button
                class="flex h-10 w-10 items-center justify-center"
                :class="
                  canGoToPreviousMonth ? 'text-blue-900' : 'text-gray-300'
                "
                :disabled="!canGoToPreviousMonth"
                :aria-label="$t('appointments.previous-month')"
                type="button"
                @click="changeCalendarMonth(-1)"
              >
                <c-icon name="ArrowBackIosRound" size="1rem" />
              </button>
              <div class="text-[15px] font-bold">
                {{ calendarMonthLabel }}
              </div>
              <button
                class="flex h-10 w-10 items-center justify-center text-blue-900"
                :aria-label="$t('appointments.next-month')"
                type="button"
                @click="changeCalendarMonth(1)"
              >
                <c-icon name="ArrowForwardIosRound" size="1rem" />
              </button>
            </div>
            <div class="mb-1 grid grid-cols-7 gap-0.5">
              <div
                v-for="(label, index) in weekdayLabels"
                :key="index"
                class="py-1 text-center text-xs font-medium text-gray-400"
              >
                {{ label }}
              </div>
            </div>
            <div class="grid grid-cols-7 gap-0.5">
              <template v-for="(day, index) in calendarDays" :key="index">
                <div v-if="!day" />
                <button
                  v-else
                  class="h-[42px] rounded-small text-[15px]"
                  :class="calendarDayClass(day)"
                  :style="
                    day.selected
                      ? { backgroundColor: buttonColor, color: buttonTextColor }
                      : null
                  "
                  :disabled="day.disabled"
                  type="button"
                  @click="selectedDay = day.timestamp"
                >
                  {{ day.label }}
                </button>
              </template>
            </div>
          </div>

          <div v-if="slotGroups.length" class="space-y-4">
            <div v-for="group in slotGroups" :key="group.key">
              <div class="mb-2 text-sm font-medium text-gray-400">
                {{ group.label }}
              </div>
              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="slot in group.slots"
                  :key="slot"
                  class="h-12 rounded-small border text-[15px] font-medium"
                  :class="
                    selectedSlot === slot
                      ? ''
                      : 'border-gray-200 bg-white text-blue-900'
                  "
                  :style="selectedChoiceStyle(selectedSlot === slot)"
                  type="button"
                  @click="selectedSlot = slot"
                >
                  {{ slot }}
                </button>
              </div>
            </div>
          </div>
          <div
            v-else
            class="rounded-small bg-gray-100 p-5 text-center font-medium leading-snug text-gray-400"
          >
            {{ $t("appointments.no-slots") }}
          </div>
        </div>

        <div v-else class="space-y-3">
          <n-input
            v-model:value="comment"
            type="textarea"
            :autosize="{ minRows: 5, maxRows: 8 }"
            :placeholder="$t('appointments.comment-placeholder')"
          />
          <div class="text-[13px] leading-normal text-gray-400">
            {{ $t("appointments.email-confirmation") }}
          </div>
        </div>
      </div>

      <div
        class="sticky bottom-0 z-20 border-t border-gray-200 bg-white px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_rgba(34,33,52,0.08)] md:rounded-b-big"
      >
        <div
          class="mb-3 flex items-center gap-2 rounded-small border border-gray-200 bg-white px-4 py-3.5"
        >
          <c-icon name="EventAvailableRound" size="1.25rem" class="shrink-0" />
          <div class="min-w-0 truncate text-sm font-medium">
            {{ summaryText }}
          </div>
        </div>
        <div class="flex gap-3">
          <button
            v-if="currentStepIndex > 0"
            class="w-[112px] rounded-small border border-gray-200 bg-white py-4 text-base font-bold text-blue-900"
            type="button"
            @click="goToStep(currentStepIndex - 1)"
          >
            {{ $t("appointments.back") }}
          </button>
          <button
            class="flex-1 rounded-small border py-4 text-lg font-bold"
            :class="
              canContinue ? '' : 'border-gray-200 bg-gray-200 text-gray-400'
            "
            :style="canContinue ? selectedChoiceStyle(true) : null"
            :disabled="!canContinue"
            type="button"
            @click="handlePrimaryAction"
          >
            {{ primaryActionText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api.js";
import { mapState, mapActions } from "pinia";
import { authStore } from "../stores/auth";
import Moment from "moment";
import { extendMoment } from "moment-range";
const moment = extendMoment(Moment);
import CIcon from "../../../core-ui/src/components/CIcon.vue";
import CLoadingSpinner from "../../../core-ui/src/components/CLoadingSpinner.vue";
import googleConversion from "@/googleConversion.js";
import utils from "@comeback/core/src/utils.js";

export default {
  name: "AppointmentPage",
  data() {
    return {
      loading: true,
      booking: false,
      services: [],
      employees: [],
      selectedEmployeeId: null,
      selectedService: null,
      currentAppointments: [],
      schedule: [],
      closedDays: [],
      minNoticeMinutes: 0,
      selectedDay: moment().startOf("day").valueOf(),
      calendarMonth: moment().startOf("month").valueOf(),
      selectedSlot: null,
      comment: "",
    };
  },
  async mounted() {
    googleConversion.parseAndStoreToken();
    if (this.$route.query.step) {
      // Steps are pushed onto history as the customer advances, so a reload
      // mid-flow has to start again from the service list.
      this.$router.replace({
        name: "appointmentPage",
        params: { ...this.$route.params },
        query: { ...this.$route.query, step: undefined },
      });
    }
    let locationUrl = `/appointments/location/${this.currentLocation.id}`;
    let [
      servicesResponse,
      employeesResponse,
      closedDaysResponse,
      appointmentsResponse,
      scheduleResponse,
    ] = await Promise.all([
      api.get(`/appointments/organization/${this.organization.id}/services`),
      api.get(`${locationUrl}/employees`),
      api.get(`${locationUrl}/closed-days`),
      api.get(`${locationUrl}/appointments`),
      api.get(`${locationUrl}/schedule`),
      this.refreshMinNotice(),
    ]);
    this.services = servicesResponse.data;
    this.employees = employeesResponse.data;
    this.closedDays = closedDaysResponse.data;
    this.currentAppointments = appointmentsResponse.data;
    this.schedule = scheduleResponse.data;
    this.selectedDay = this.firstOpenDay();
    this.calendarMonth = moment(this.selectedDay).startOf("month").valueOf();
    this.loading = false;
  },
  computed: {
    ...mapState(authStore, [
      "organization",
      "currentLocation",
      "currentCustomer",
      "branding",
    ]),
    steps() {
      return [
        { key: "service", label: this.$t("appointments.step-service") },
        { key: "datetime", label: this.$t("appointments.step-date-time") },
        { key: "comment", label: this.$t("appointments.step-comment") },
      ];
    },
    currentStepIndex() {
      if (!this.selectedService) return 0;
      return Math.max(
        0,
        this.steps.findIndex((step) => step.key === this.$route.query.step)
      );
    },
    currentStep() {
      return this.steps[this.currentStepIndex];
    },
    isFinalStep() {
      return this.currentStepIndex === this.steps.length - 1;
    },
    canContinue() {
      if (this.booking) return false;
      if (this.currentStep.key === "service") return !!this.selectedService;
      return !!this.selectedSlot;
    },
    primaryActionText() {
      if (this.isFinalStep) return this.$t("appointments.title");
      return this.$t("appointments.continue");
    },
    summaryText() {
      if (!this.selectedService) return this.$t("appointments.pick-service");
      if (!this.selectedSlot) return this.$t("appointments.pick-date-time");
      return this.$t("appointments.summary", {
        service: this.selectedService.name,
        date: moment(this.selectedDay).format("DD/MM"),
        time: this.selectedSlot,
      });
    },
    showServiceImages() {
      let servicesWithImage = this.services.filter(
        (service) => service.imageId
      ).length;
      return (
        servicesWithImage > 0 && servicesWithImage * 2 >= this.services.length
      );
    },
    showEmployeeSelector() {
      return this.serviceEmployees.length >= 2;
    },
    employeeSelectorOptions() {
      return [
        {
          id: null,
          name: this.$t("appointments.anyone"),
          photoImageId: null,
        },
        ...this.serviceEmployees,
      ];
    },
    weekdayLabels() {
      let formatter = new Intl.DateTimeFormat(this.$i18n.locale, {
        weekday: "short",
      });
      let monday = moment().startOf("isoWeek");
      return Array.from({ length: 7 }, (_, index) =>
        this.capitalize(
          formatter.format(monday.clone().add(index, "days").toDate())
        )
      );
    },
    calendarMonthLabel() {
      return this.capitalize(
        new Intl.DateTimeFormat(this.$i18n.locale, {
          month: "long",
          year: "numeric",
        }).format(new Date(this.calendarMonth))
      );
    },
    calendarDays() {
      let month = moment(this.calendarMonth);
      let days = Array(month.isoWeekday() - 1).fill(null);
      for (let date = 1; date <= month.daysInMonth(); date++) {
        let day = month.clone().date(date);
        days.push({
          timestamp: day.valueOf(),
          label: date,
          disabled: this.dayIsDisabled(day.valueOf()),
          selected: day.isSame(this.selectedDay, "day"),
          today: day.isSame(moment(), "day"),
        });
      }
      return days;
    },
    canGoToPreviousMonth() {
      return moment(this.calendarMonth).isAfter(
        this.earliestBookableTime(),
        "month"
      );
    },
    slotGroups() {
      let groups = [
        { key: "morning", label: this.$t("appointments.morning"), slots: [] },
        {
          key: "afternoon",
          label: this.$t("appointments.afternoon"),
          slots: [],
        },
        { key: "evening", label: this.$t("appointments.evening"), slots: [] },
      ];
      this.selectedDayAvailableSlots.forEach((slot) => {
        let hour = Number(slot.split(":")[0]);
        let group = hour < 12 ? groups[0] : hour < 17 ? groups[1] : groups[2];
        group.slots.push(slot);
      });
      return groups.filter((group) => group.slots.length);
    },
    buttonColor() {
      let accentColor = this.branding?.accentColor || "#545383";
      let backgroundColor = this.branding?.backgroundColor || "#FFFFFF";
      if (utils.colorIsDark(accentColor)) return accentColor;
      return utils.colorIsDark(backgroundColor) ? backgroundColor : accentColor;
    },
    buttonTextColor() {
      return utils.colorIsDark(this.buttonColor) ? "#FFFFFF" : "#222134";
    },
    buttonBorderColor() {
      return utils.colorIsDark(this.buttonColor) ? this.buttonColor : "#222134";
    },
    appointmentEmployeeCandidates() {
      if (this.selectedEmployeeId) {
        return this.serviceEmployees.filter(
          (employee) => employee.id === this.selectedEmployeeId
        );
      }
      return this.serviceEmployees;
    },
    serviceEmployees() {
      if (!this.selectedService?.employeeIds?.length) {
        return this.employees;
      }
      let employeeIds = new Set(this.selectedService.employeeIds || []);
      return this.employees.filter((employee) => employeeIds.has(employee.id));
    },
    servicesById() {
      return this.services?.reduce((acc, service) => {
        acc[service.id] = service;
        return acc;
      }, {});
    },
    selectedDayAvailableSlots() {
      if (!this.selectedService) return [];
      return this.getSlots(this.selectedDay);
    },
  },
  methods: {
    ...mapActions(authStore, ["updateAppointment"]),
    goToStep(index) {
      if (index < 0 || index >= this.currentStepIndex) return;
      // Every step after the first is its own history entry, so going back
      // through history keeps the browser back button in sync with the UI.
      this.$router.go(index - this.currentStepIndex);
    },
    goNextStep() {
      this.$router.push({
        name: "appointmentPage",
        params: { ...this.$route.params },
        query: {
          ...this.$route.query,
          step: this.steps[this.currentStepIndex + 1].key,
        },
      });
    },
    handlePrimaryAction() {
      if (!this.canContinue) return;
      if (this.isFinalStep) {
        this.bookAppointment();
        return;
      }
      this.goNextStep();
    },
    async bookAppointment() {
      if (!this.selectedSlot || this.dayIsClosed(this.selectedDay)) return;
      let employeeId = this.getAvailableEmployeeIdForSlot(this.selectedSlot);
      if (!employeeId) return;
      let startTimeInLocation = `${moment(this.selectedDay).format(
        "YYYY-MM-DD"
      )}T${this.selectedSlot}:00`;
      if (moment(startTimeInLocation).isBefore(this.earliestBookableTime())) {
        return;
      }
      let appointment = {
        serviceId: this.selectedService.id,
        startTimeInLocation,
        comment: this.comment,
        employeeId,
      };
      this.updateAppointment(appointment);
      if (!this.currentCustomer?.id) {
        this.$router.push({
          name: "createCustomerPage",
          params: {
            ...this.$route.params,
          },
        });
        return;
      }
      this.booking = true;
      try {
        await api.post(
          `/appointments/location/${this.currentLocation.id}/appointment`,
          { ...appointment, customerId: this.currentCustomer.id }
        );
        googleConversion.sendConversionEvent();
        this.$router.push({
          name: "downloadPage",
          params: {
            ...this.$route.params,
          },
        });
      } catch (e) {
        if (e.response?.data?.error === "appointments_closed_day") {
          this.$notificationGenerator.error(
            this.$t("appointments.closed-day-error")
          );
        } else if (e.response?.data?.error === "appointment_min_notice") {
          this.$notificationGenerator.error(
            this.$t("appointments.min-notice-error")
          );
        } else {
          this.$notificationGenerator.error();
        }
        if (
          [
            "appointment_employee_unavailable",
            "appointment_time_unavailable",
            "appointment_min_notice",
          ].includes(e.response?.data?.error)
        ) {
          await this.refreshMinNotice();
          let [appointmentsResponse, employeesResponse] = await Promise.all([
            api.get(
              `/appointments/location/${this.currentLocation.id}/appointments`
            ),
            api.get(
              `/appointments/location/${this.currentLocation.id}/employees`
            ),
          ]);
          this.currentAppointments = appointmentsResponse.data;
          this.employees = employeesResponse.data;
          this.updateSelectedSlot();
          if (!this.selectedSlot) this.goToStep(1);
        }
      } finally {
        this.booking = false;
      }
    },
    serviceIsSelected(service) {
      return this.selectedService?.id === service.id;
    },
    serviceCardStyle(service) {
      if (!this.serviceIsSelected(service)) return null;
      return {
        borderColor: this.buttonColor,
        boxShadow: `0 0 0 1px ${this.buttonColor}`,
      };
    },
    serviceImageUrl(service) {
      return `https://res.cloudinary.com/comebackapp/image/upload/f_auto,q_auto,w_128,h_128,c_fill/${service.imageId}`;
    },
    selectedChoiceStyle(selected) {
      if (!selected) return null;
      return {
        backgroundColor: this.buttonColor,
        borderColor: this.buttonBorderColor,
        color: this.buttonTextColor,
      };
    },
    calendarDayClass(day) {
      if (day.selected) return "font-bold";
      return [
        day.disabled ? "text-gray-300" : "text-blue-900",
        { "font-bold underline": day.today },
      ];
    },
    changeCalendarMonth(delta) {
      if (delta < 0 && !this.canGoToPreviousMonth) return;
      this.calendarMonth = moment(this.calendarMonth)
        .add(delta, "months")
        .valueOf();
    },
    capitalize(text) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    employeeInitial(employee) {
      return (employee.name || "").trim().charAt(0).toUpperCase();
    },
    slotIsInWorkingHours(slot, daySchedule) {
      let workingRanges = daySchedule.map((schedule) => {
        let [startHours, startMinutes] = schedule.startTime
          .split(":")
          .map(Number);
        let [endHours, endMinutes] = schedule.endTime.split(":").map(Number);
        return moment.range(
          moment(slot).set({ hours: startHours, minutes: startMinutes }),
          moment(slot)
            .set({ hours: endHours, minutes: endMinutes })
            .subtract(this.selectedService.durationMinutes, "minutes")
        );
      });
      return workingRanges.some((range) => range.contains(moment(slot)));
    },
    getSlots(day) {
      let slots = [];
      let slotMinutes = 15;
      if (this.dayIsClosed(day)) return [];
      let daySchedule = this.schedule[moment(day).weekday()];
      if (!daySchedule) return [];
      let appointmentsFromDay = this.currentAppointments.filter((appointment) =>
        moment(appointment.localStartTime || appointment.startTime).isSame(
          moment(day),
          "day"
        )
      );
      let time = moment(day).set({
        hours: 0,
        minutes: 0,
        seconds: 0,
      });
      let earliestBookableTime = this.earliestBookableTime();
      for (let i = 0; i < 100; i++) {
        if (
          !time.isBefore(earliestBookableTime) &&
          !this.slotFilled({
            appointmentsFromDay,
            daySchedule,
            slot: time,
          })
        ) {
          slots.push(time.format("HH:mm"));
        }
        time.add(slotMinutes, "minutes");
      }
      return slots;
    },
    handleEmployeeSelected(employeeId) {
      this.selectedEmployeeId = employeeId;
      this.updateSelectedSlot();
    },
    updateSelectedSlot() {
      if (!this.selectedDayAvailableSlots.includes(this.selectedSlot)) {
        this.selectedSlot = null;
      }
    },
    employeePhotoUrl(employee) {
      return `https://res.cloudinary.com/comebackapp/image/upload/f_auto,q_auto,w_128,h_128,c_fill/${employee.photoImageId}`;
    },
    handleServiceSelected(service) {
      if (this.serviceIsSelected(service)) return;
      this.selectedService = service;
      this.selectedEmployeeId = null;
      this.selectedSlot = null;
    },
    dayIsDisabled(timestamp) {
      let firstBookableDay = this.earliestBookableTime()
        .startOf("day")
        .valueOf();
      return (
        timestamp < firstBookableDay ||
        !this.schedule[moment(timestamp).weekday()] ||
        this.dayIsClosed(timestamp)
      );
    },
    firstOpenDay() {
      let day = this.earliestBookableTime().startOf("day");
      for (let i = 0; i < 366; i++) {
        if (!this.dayIsDisabled(day.valueOf())) return day.valueOf();
        day.add(1, "day");
      }
      return this.earliestBookableTime().startOf("day").valueOf();
    },
    earliestBookableTime() {
      return moment().add(this.minNoticeMinutes, "minutes");
    },
    async refreshMinNotice() {
      let response = await api.get(
        `/appointments/organization/${this.organization.id}/config`
      );
      this.minNoticeMinutes = response.data.minNoticeMinutes || 0;
    },
    dayIsClosed(day) {
      if (!day) return false;
      let dateKey = moment(day).format("YYYY-MM-DD");
      return this.closedDays.some((closedDay) =>
        this.closedDayMatchesDate(closedDay, dateKey)
      );
    },
    closedDayMatchesDate(closedDay, dateKey) {
      let startDate = closedDay.startDate;
      let endDate = closedDay.endDate || closedDay.startDate;
      if (!startDate || !endDate || !dateKey) return false;
      if (closedDay.yearly) {
        let dayMonth = dateKey.slice(5);
        let startDayMonth = startDate.slice(5);
        let endDayMonth = endDate.slice(5);
        if (startDayMonth <= endDayMonth) {
          return dayMonth >= startDayMonth && dayMonth <= endDayMonth;
        }
        return dayMonth >= startDayMonth || dayMonth <= endDayMonth;
      }
      return dateKey >= startDate && dateKey <= endDate;
    },
    getAvailableEmployeeIdForSlot(slot) {
      if (!slot) return null;
      let appointmentsFromDay = this.currentAppointments.filter((appointment) =>
        moment(appointment.localStartTime || appointment.startTime).isSame(
          moment(this.selectedDay),
          "day"
        )
      );
      let daySchedule = this.schedule[moment(this.selectedDay).weekday()];
      let slotMoment = moment(this.selectedDay).set({
        hours: Number(slot.split(":")[0]),
        minutes: Number(slot.split(":")[1]),
        seconds: 0,
      });
      let employee = this.availableEmployeesForSlot({
        appointmentsFromDay,
        daySchedule,
        slot: slotMoment,
      })[0];
      return employee?.id || null;
    },
    availableEmployeesForSlot({ appointmentsFromDay, daySchedule, slot }) {
      return this.appointmentEmployeeCandidates.filter((employee) =>
        this.employeeIsAvailableForSlot({
          appointmentsFromDay,
          daySchedule,
          employee,
          slot,
        })
      );
    },
    getEmployeeScheduleForDate(employee, dateKey) {
      return employee.schedules?.find((schedule) => {
        if (dateKey < schedule.startDate) return false;
        return !schedule.endDate || dateKey <= schedule.endDate;
      });
    },
    getEmployeeScheduleIntervalsForSlot(employee, slot) {
      let dateKey = moment(slot).format("YYYY-MM-DD");
      let schedule = this.getEmployeeScheduleForDate(employee, dateKey);
      if (!schedule) return [];
      let scheduleWeek = moment(schedule.startDate).startOf("isoWeek");
      let selectedWeek = moment(dateKey).startOf("isoWeek");
      let weekOffset = selectedWeek.diff(scheduleWeek, "weeks");
      let weekIndex = weekOffset % schedule.recurrenceWeeks;
      let dayIndex = moment(slot).isoWeekday() - 1;
      return schedule.weeks?.[weekIndex]?.[dayIndex] || [];
    },
    employeeIsAvailableForSlot({
      appointmentsFromDay,
      daySchedule,
      employee,
      slot,
    }) {
      if (!this.slotIsInWorkingHours(slot, daySchedule)) {
        return false;
      }
      let employeeSchedule = this.getEmployeeScheduleIntervalsForSlot(
        employee,
        slot
      );
      if (!this.slotIsInWorkingHours(slot, employeeSchedule)) {
        return false;
      }
      return !appointmentsFromDay.some((appointment) => {
        let appointmentDurationMinutes =
          this.servicesById[appointment.serviceId]?.durationMinutes ||
          appointment.durationMinutes ||
          this.selectedService.durationMinutes;
        let range1 = moment.range(
          moment(appointment.localStartTime || appointment.startTime),
          moment(appointment.localStartTime || appointment.startTime).add(
            appointmentDurationMinutes,
            "minutes"
          )
        );
        let range2 = moment.range(
          moment(slot),
          moment(slot).add(this.selectedService.durationMinutes, "minutes")
        );
        if (!range1.overlaps(range2)) return false;
        return (
          !appointment.employeeId || appointment.employeeId === employee.id
        );
      });
    },
    slotFilled({ appointmentsFromDay, daySchedule, slot }) {
      return (
        this.availableEmployeesForSlot({
          appointmentsFromDay,
          daySchedule,
          slot,
        }).length === 0
      );
    },
  },
  components: {
    CIcon,
    CLoadingSpinner,
  },
  watch: {
    selectedDay() {
      this.selectedSlot = null;
    },
    employees() {
      this.updateSelectedSlot();
    },
    currentStepIndex() {
      window.scrollTo({ top: 0 });
    },
  },
};
</script>
